/* Independent motion layers; no dependencies, online requests, or idle animation loop. */
(() => {
  if (customElements.get('az-snack-hero')) return;
  class AZSnackHero extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      this.lifecycle = new AbortController();
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.pointer = matchMedia('(hover: hover) and (pointer: fine)');
      this.animations = new Set();
      this.revealed ||= new WeakSet();
      this.scene = this.querySelector('.az-snacks__scene');
      // Map both pointer axes so some layers travel across the pointer direction.
      const directions = { follow: [1, 0, 0, 1], reverse: [-1, 0, 0, -1], mirror_x: [-1, 0, 0, 1], mirror_y: [1, 0, 0, -1], clockwise: [0, -1, 1, 0], counter_clockwise: [0, 1, -1, 0] };
      this.layers = [...this.querySelectorAll('.az-snacks__layer')].map(element => ({
        element,
        direction: directions[element.dataset.direction] || directions.follow,
        depth: Number.isFinite(Number(element.dataset.depth)) ? Number(element.dataset.depth) : 1,
        response: Math.max(80, Math.min(600, Number(element.dataset.response) || 180)),
        x: 0, y: 0
      }));
      const options = { signal: this.lifecycle.signal };
      this.reduced.addEventListener('change', () => this.configure(), options);
      this.pointer.addEventListener('change', () => this.configure(), options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.reset();
      }, options);
      const editor = this.closest('.shopify-section') || this;
      editor.addEventListener('shopify:block:select', () => this.finishReveal(), options);
      editor.addEventListener('shopify:section:select', () => this.finishReveal(), options);
      this.configure();
    }

    configure() {
      this.motion?.abort();
      this.visibility?.disconnect();
      this.revealObserver?.disconnect();
      this.finishReveal();
      this.reset();
      this.motion = new AbortController();
      const options = { passive: true, signal: this.motion.signal };
      if (this.scene && !this.reduced.matches && this.pointer.matches && this.dataset.parallax === 'true') {
        this.addEventListener('pointermove', event => {
          if (event.pointerType === 'touch' || document.hidden || this.reduced.matches) return;
          const bounds = this.getBoundingClientRect();
          const strength = Number(this.dataset.strength) || 0;
          this.targetX = Math.max(-1, Math.min(1, (event.clientX - bounds.left) / bounds.width * 2 - 1)) * strength;
          this.targetY = Math.max(-1, Math.min(1, (event.clientY - bounds.top) / bounds.height * 2 - 1)) * strength;
          this.requestTick();
        }, options);
        this.addEventListener('pointerleave', () => {
          this.targetX = this.targetY = 0;
          this.requestTick();
        }, options);
        this.addEventListener('pointercancel', () => this.reset(), options);
      }
      if (!('IntersectionObserver' in window)) return;
      this.visibility = new IntersectionObserver(entries => {
        if (!entries[0].isIntersecting) this.reset();
      });
      this.visibility.observe(this);
      if (this.reduced.matches || this.dataset.reveal !== 'true' || window.Shopify?.designMode) return;
      this.revealObserver = new IntersectionObserver(entries => {
        let delay = 0;
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          this.revealObserver.unobserve(entry.target);
          if (this.revealed.has(entry.target)) return;
          this.revealed.add(entry.target);
          this.revealTarget(entry.target, delay++ * 60, this.motion.signal);
        });
      }, { threshold: .1 });
      this.querySelectorAll('[data-snack-reveal]').forEach(target => this.revealObserver.observe(target));
    }

    async revealTarget(target, delay, signal) {
      if (!target.animate) return;
      const image = target.querySelector('.az-snacks__image');
      if (image) {
        // Keep touch bitmaps in normal painting: no transient opacity/scale surface
        // while a lazy, transparent image is being decoded by the mobile browser.
        if (!this.pointer.matches || typeof image.decode !== 'function') return;
        try { await image.decode(); } catch { return; }
      }
      // Settings changes and section removal invalidate pending decode promises.
      if (signal.aborted || !this.isConnected || this.reduced.matches) return;
      const animation = target.animate([
        { opacity: 0, transform: 'translateY(16px) scale(.97)' },
        { opacity: 1, transform: 'translateY(0) scale(1)' }
      ], { duration: Number(this.dataset.duration) || 650, delay, easing: 'cubic-bezier(.2,.65,.3,1)', fill: 'backwards' });
      this.animations.add(animation);
      animation.onfinish = animation.oncancel = () => this.animations.delete(animation);
    }

    requestTick() {
      if (!this.frame) this.frame = requestAnimationFrame(time => this.tick(time));
    }

    tick(time) {
      this.frame = 0;
      if (!this.isConnected || !this.scene) return;
      if (this.reduced.matches || document.hidden) { this.reset(); return; }
      // Time-based smoothing keeps the response consistent at different refresh rates.
      const delta = this.lastTick ? Math.min(48, Math.max(1, time - this.lastTick)) : 1000 / 60;
      this.lastTick = time;
      const glowResponse = 1 - Math.exp(-delta / 110);
      this.x += (this.targetX - this.x) * glowResponse;
      this.y += (this.targetY - this.y) * glowResponse;
      let moving = Math.abs(this.targetX - this.x) > .03 || Math.abs(this.targetY - this.y) > .03;
      this.layers.forEach(layer => {
        const targetX = (this.targetX * layer.direction[0] + this.targetY * layer.direction[1]) * layer.depth;
        const targetY = (this.targetX * layer.direction[2] + this.targetY * layer.direction[3]) * layer.depth;
        const response = 1 - Math.exp(-delta / layer.response);
        layer.x += (targetX - layer.x) * response;
        layer.y += (targetY - layer.y) * response;
        const unsettled = Math.abs(targetX - layer.x) > .03 || Math.abs(targetY - layer.y) > .03;
        if (!unsettled) { layer.x = targetX; layer.y = targetY; }
        moving ||= unsettled;
        layer.element.style.setProperty('--snack-layer-x', `${layer.x.toFixed(2)}px`);
        layer.element.style.setProperty('--snack-layer-y', `${layer.y.toFixed(2)}px`);
      });
      this.scene.style.setProperty('--snack-motion-x', `${this.x.toFixed(2)}px`);
      this.scene.style.setProperty('--snack-motion-y', `${this.y.toFixed(2)}px`);
      if (moving) this.requestTick();
      else this.lastTick = 0;
    }

    reset() {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.x = this.y = this.targetX = this.targetY = 0;
      this.lastTick = 0;
      this.layers?.forEach(layer => {
        layer.x = layer.y = 0;
        layer.element.style.removeProperty('--snack-layer-x');
        layer.element.style.removeProperty('--snack-layer-y');
      });
      this.scene?.style.removeProperty('--snack-motion-x');
      this.scene?.style.removeProperty('--snack-motion-y');
    }

    finishReveal() {
      this.animations?.forEach(animation => animation.cancel());
      this.animations?.clear();
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.motion?.abort();
      this.visibility?.disconnect();
      this.revealObserver?.disconnect();
      this.finishReveal();
      this.reset();
      this.lifecycle = null;
    }
  }
  customElements.define('az-snack-hero', AZSnackHero);
})();
